import { ReactNode, forwardRef, useEffect, useId, useRef, useState } from "react"
import FormField, { fieldControlA11yProps } from "lib/components/Form/FormField"
import InputShell from "lib/components/Form/InputShell"
import { resolveFieldStatus } from "lib/components/Form/fieldStyles"
import dynamic from "next/dynamic"

import "react-phone-input-2/lib/style.css"

const PhoneInput: any = dynamic(() => import("react-phone-input-2"), { ssr: false })

export interface InputPhoneProps {
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  value?: string
  onChange?: (value: string | undefined) => void
  name?: string
  disabled?: boolean
  success?: boolean
  error?: boolean
  message?: string
  /** Neutral hint under the box; replaced by `message` while `error` is set. */
  helperText?: ReactNode
  defaultCountry?: any
  countryCodeEditable?: boolean
  enableSearch?: boolean
  enableAreaCodes?: boolean
  disableSearchIcon?: boolean
}

/**
 * Restyles react-phone-input-2's country button into a design-system left addon.
 *
 * The vendor stylesheet imported above matches these `[&_.x]` variants on specificity, so every
 * rule carries `!` or the vendor wins on source order. Kept as literal strings so Tailwind's
 * scanner emits them.
 */
const VENDOR_OVERRIDES = [
  // Size against the shell's 34px content box, never its 36px border box: a 36px child overflows
  // by 1px top and bottom and paints over the shell's own border.
  "h-full w-full",
  "[&_.form-control]:!h-full [&_.form-control]:!w-full [&_.form-control]:!rounded-none",
  "[&_.form-control]:!border-0 [&_.form-control]:!bg-transparent [&_.form-control]:!shadow-none",
  "[&_.form-control]:!pl-[60px] [&_.form-control]:!pr-2 [&_.form-control]:!text-sm",
  "[&_.form-control]:placeholder:text-neutral-50",
  "[&_.flag-dropdown]:!rounded-l-[5px] [&_.flag-dropdown]:!border-0 [&_.flag-dropdown]:!border-r",
  "[&_.flag-dropdown]:!border-neutral-40",
  "[&_.selected-flag]:!w-[52px] [&_.selected-flag]:!rounded-l-[5px] [&_.selected-flag]:!px-2",
  "[&_.country-list]:!rounded-md [&_.country-list]:!text-sm",
].join(" ")

const ENABLED_ADDON =
  "[&_.form-control]:!text-neutral-70 [&_.flag-dropdown]:!bg-neutral-20 [&_.selected-flag]:!bg-neutral-20 [&_.selected-flag:hover]:!bg-neutral-20 [&_.selected-flag:focus]:!bg-neutral-20 [&_.flag-dropdown.open]:!bg-neutral-20 [&_.flag-dropdown.open_.selected-flag]:!bg-neutral-20"

const DISABLED_ADDON =
  "[&_.flag-dropdown]:!bg-transparent [&_.selected-flag]:!bg-transparent [&_.form-control]:!text-neutral-50"

const InputPhone = forwardRef<HTMLInputElement, InputPhoneProps>(({
  label,
  placeholder = "Enter phone number",
  searchPlaceholder = "Search",
  value,
  onChange,
  name,
  disabled = false,
  success,
  error,
  message,
  helperText,
  defaultCountry = "id",
  countryCodeEditable,
  enableSearch,
  disableSearchIcon,
  enableAreaCodes = true,
}, ref) => {
  const [displayValue, setDisplayValue] = useState("")
  const inputRef = useRef<HTMLInputElement | null>(null)
  const controlId = useId()
  const status = resolveFieldStatus(error, success)
  const helper = status === "danger" && message ? message : helperText

  useEffect(() => {
    setDisplayValue(value || "")
  }, [value])

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const pastedText = e.clipboardData?.getData("Text") ?? ""
      const numericText = pastedText.replace(/\D/g, "")

      if (numericText.startsWith("0")) {
        const newValue = `62${numericText.slice(1)}`
        e.preventDefault()
        setDisplayValue(`+${newValue}`)
        onChange?.(newValue)
      }
    }

    const input = inputRef.current
    if (input) {
      input.addEventListener("paste", handlePaste)
    }

    return () => {
      if (input) {
        input.removeEventListener("paste", handlePaste)
      }
    }
  }, [onChange])

  const handleOnChange = (input: string, country: any) => {
    let rawPhone = input.slice(country.dialCode.length)

    if (rawPhone.startsWith("0")) {
      rawPhone = rawPhone.slice(1)
    }

    const sanitized = `${country.dialCode}${rawPhone}`
    setDisplayValue(`+${sanitized}`)
    onChange?.(sanitized)
  }

  return (
    <FormField
      controlId={controlId}
      label={label}
      name={name}
      status={status}
      disabled={disabled}
      helperText={helperText}
      message={message}>
      <InputShell status={status} disabled={disabled}>
        <PhoneInput
          country={defaultCountry}
          value={displayValue}
          onChange={handleOnChange}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          containerClass={`${VENDOR_OVERRIDES} ${disabled ? DISABLED_ADDON : ENABLED_ADDON}`}
          disabled={disabled}
          disableDropdown={disabled}
          enableSearch={enableSearch}
          disableSearchIcon={disableSearchIcon}
          countryCodeEditable={countryCodeEditable}
          enableAreaCodes={enableAreaCodes}
          inputProps={{
            name,
            ...fieldControlA11yProps({ controlId, status, hasHelper: Boolean(helper) }),
            ref: (el: HTMLInputElement) => {
              inputRef.current = el
              if (typeof ref === "function") ref(el)
              else if (ref) (ref as React.MutableRefObject<HTMLInputElement>).current = el
            },
          }}
        />
      </InputShell>
    </FormField>
  )
})

InputPhone.displayName = "InputPhone"

export default InputPhone
